/* =====================================================================
   AstaLab 2.0 — BackendAdapter: contratto unico dietro cui vivono sia il
   backend reale (SupabaseBackendAdapter) sia quello demo dell'Artifact
   (ArtifactDemoBackendAdapter). Login, dashboard, multi-asta e tutte le
   future schermate parlano SOLO con questa interfaccia — mai
   direttamente con supabase-js o con localStorage. Questo è il punto
   che rende vero "stessa UI, stessa business logic, cambia solo
   l'adapter" (vedi bootstrap.js per l'UNICO punto che sceglie quale).

   Ogni metodo qui sotto è documentato con la forma del valore restituito
   e degli errori attesi: entrambe le implementazioni devono rispettarla
   esattamente, altrimenti la UI condivisa smette di essere davvero
   condivisa. Un metodo non implementato da una sottoclasse lancia
   esplicitamente, non fallisce silenziosamente con `undefined`.
   ===================================================================== */

function notImplemented(method){
  throw new Error(`BackendAdapter.${method}() non implementato da questo adapter`);
}

/**
 * Round Beta-P3-3 (design D4, accettato): normalizzazione UNICA usata per correlare un tentativo
 * locale signIn/signUp con l'identità riportata da un evento onAuthStateChange — mai un confronto
 * fra un'email grezza e un'altra grezza. Applicata in modo identico su ENTRAMBI i lati di ogni
 * confronto (inizio tentativo E lettura dell'evento), in entrambi gli adapter: la correttezza non
 * dipende mai dal sapere come Supabase canonicalizza internamente le email, solo dal fatto che
 * questa stessa funzione pura sia applicata due volte in modo coerente. Stessa disciplina
 * trim+lowercase già usata da ArtifactDemoBackendAdapter per il proprio controllo email-duplicata —
 * nessuna nuova convenzione inventata qui. Deliberatamente NON estesa a una normalizzazione
 * Unicode/NFC: quel gap è ereditato dal comportamento email esistente in questo codebase, non una
 * nuova garanzia introdotta da questo round.
 */
export function normalizeEmailForCorrelation(email){
  return String(email || "").trim().toLowerCase();
}

/**
 * Round Beta-P3-3 (design D4): contatore PER-ISTANZA (mai persistito — localStorage/Supabase/
 * cross-tab avrebbero rotto esattamente l'indipendenza fra tab che questo meccanismo deve
 * preservare) dei tentativi signIn/signUp locali attualmente in volo, per identità email
 * normalizzata. Un Map<chiave,refcount>, MAI un Set/booleano/singola-email-corrente: due tentativi
 * per la STESSA email possono sovrapporsi genuinamente (un vecchio /login abbandonato ancora in
 * volo + un nuovo tentativo legittimo dopo essere tornati su /login) — un Set cancellerebbe la
 * chiave quando il PRIMO dei due termina, anche se il secondo (ancora attivo/legittimo) ne ha
 * ancora bisogno. Il refcount supporta una profondità di sovrapposizione arbitraria.
 */
export class LocalAuthAttemptTracker{
  constructor(){ this._counts = new Map(); }
  begin(email){
    const key = normalizeEmailForCorrelation(email);
    this._counts.set(key, (this._counts.get(key) || 0) + 1);
  }
  end(email){
    const key = normalizeEmailForCorrelation(email);
    const n = (this._counts.get(key) || 1) - 1;
    if(n <= 0) this._counts.delete(key); else this._counts.set(key, n);
  }
  isInFlight(email){
    return this._counts.has(normalizeEmailForCorrelation(email));
  }
}

/**
 * @typedef {Object} AuthResult
 * @property {boolean} ok
 * @property {{id:string, email:string}|null} user
 * @property {string|null} error - messaggio in italiano, pronto per un toast, mai un dettaglio tecnico interno
 */

/**
 * @typedef {Object} AuctionSummary
 * @property {string} id
 * @property {string} name
 * @property {string|null} season
 * @property {"active"|"archived"} status
 * @property {string} ownerId
 * @property {"owner"|"member"} myRole
 * @property {string} createdAt
 * @property {string} updatedAt
 */

/**
 * @typedef {Object} AuctionStateEnvelope
 * @property {string} auctionId
 * @property {number} revision - contatore monotono per la concorrenza ottimistica (obiettivo 8)
 * @property {string} updatedAt
 * @property {Object} state - lo stato applicativo AstaLab (stessa forma di STATE nella 1.3, vedi repository/AuctionRepository.js)
 */

export class BackendAdapter{
  /** Etichetta leggibile, usata solo per diagnostica/log e per il banner di modalità — mai per decisioni di business logic. */
  get kind(){ notImplemented("kind (getter)"); }

  // ---------------- Auth (obiettivo 3) ----------------
  /** @returns {Promise<AuthResult>} */
  async signUp(email, password){ return notImplemented("signUp"); }
  /** @returns {Promise<AuthResult>} */
  async signIn(email, password){ return notImplemented("signIn"); }
  /** @returns {Promise<{ok:boolean, error:string|null}>} */
  async signOut(){ return notImplemented("signOut"); }
  /**
   * Round Beta-P3-4 (design D/D2, accettato): contratto rivisto per distinguere lo stato
   * AUTORITATIVO "non autenticato" da una lettura semplicemente INCERTA — mai collassati insieme.
   * Tracciato sulla semantica esatta della sorgente installata (@supabase/auth-js 2.112.2,
   * GoTrueClient#_getUser): l'UNICA condizione che il SDK stesso tratta come autorevolmente "utente
   * disconnesso" è un errore `AuthSessionMissingError` (nessun access token locale/nessuna sessione
   * — la stessa condizione per cui il SDK chiama internamente `_removeSession()`, unico caso in cui
   * lo fa). Ogni ALTRO esito — un altro AuthError (token invalso/scaduto rifiutato dal server,
   * fallimento di rete, errore sconosciuto) o un'eccezione inattesa — NON è equivalente a "non
   * autenticato": è un'incertezza che il chiamante deve trattare con un retry veritiero, mai con un
   * redirect silenzioso al login (vedi le pagine protette: DashboardPage/CatalogPage/
   * AuctionDetailPage/app.js). Nessun oggetto di risultato più ricco introdotto: la forma pubblica
   * resta `User|null`, con il rigetto della promise a portare l'incertezza.
   * @returns {Promise<{id:string, email:string}|null>} l'utente della sessione corrente; `null`
   *   SOLO quando lo stato "non autenticato" è autoritativo (mai per una lettura fallita/incerta).
   *   La promise viene RIGETTATA quando lo stato di autenticazione corrente non può essere
   *   determinato con certezza — il chiamante non deve mai interpretare un rigetto come "non
   *   autenticato".
   */
  async getCurrentUser(){ return notImplemented("getCurrentUser"); }
  /** @returns {Promise<{ok:boolean, error:string|null}>} invia l'email di recupero password */
  async requestPasswordReset(email){ return notImplemented("requestPasswordReset"); }
  /** @returns {Promise<{ok:boolean, error:string|null}>} aggiorna la password dell'utente autenticato (dopo il link di recupero) */
  async updatePassword(newPassword){ return notImplemented("updatePassword"); }
  /**
   * Revisione r1 (obiettivo 8): il callback riceve (event, user), non solo user — il TIPO di
   * evento è indispensabile per distinguere un normale SIGNED_IN da un PASSWORD_RECOVERY (l'utente
   * ha cliccato il link di recupero ed è arrivato con una sessione temporanea: la UI deve
   * navigare a /reset-password, non a /dashboard). Eventi minimi che ogni adapter deve propagare:
   * "SIGNED_IN", "SIGNED_OUT", "USER_UPDATED"; SupabaseBackendAdapter propaga anche
   * "INITIAL_SESSION"/"PASSWORD_RECOVERY"/"TOKEN_REFRESHED" (valori nativi di supabase-js).
   *
   * Round Beta-P3-3 (design D4, accettato): terzo argomento ADDITIVO, mai un cambio incompatibile —
   * un callback esistente con arità (event) o (event,user) continua a funzionare identicamente,
   * ignorando semplicemente l'argomento in più.
   * `meta.isLocalInteractiveAuthInFlight` (boolean) — vero quando, nell'ISTANTE in cui questo
   * evento è stato osservato, QUESTA istanza di adapter aveva almeno una chiamata locale
   * signIn()/signUp() ancora in volo per la STESSA identità normalizzata (vedi
   * normalizeEmailForCorrelation/LocalAuthAttemptTracker sopra). Semantica precisa, mai
   * sovra-dichiarata: questo campo NON significa "questo evento è stato causato da quella
   * chiamata" — significa solo che una coincidenza temporale/di identità esiste. In particolare, un
   * evento SIGNED_IN genuinamente esterno (sync fra tab) per la STESSA email di un tentativo locale
   * abbandonato leggerebbe comunque true qui: limite noto, documentato, mai preteso perfetto (vedi
   * il commento su app.js). Per un evento senza alcun tentativo locale in volo per quella identità
   * (incluso ogni evento diverso da SIGNED_IN, o un'identità diversa), `meta` è `{
   * isLocalInteractiveAuthInFlight:false }`.
   * @param {(event:string, user:{id:string,email:string}|null, meta?:{isLocalInteractiveAuthInFlight:boolean})=>void} callback
   * @returns {()=>void} funzione di unsubscribe
   */
  onAuthStateChange(callback){ return notImplemented("onAuthStateChange"); }

  // ---------------- Profilo utente ----------------
  /** @returns {Promise<{id:string, displayName:string, createdAt:string}|null>} */
  async getProfile(userId){ return notImplemented("getProfile"); }
  /** @returns {Promise<{ok:boolean, error:string|null}>} */
  async updateProfile(userId, patch){ return notImplemented("updateProfile"); }

  // ---------------- Multi-asta (obiettivo 6) ----------------
  /** @returns {Promise<AuctionSummary[]>} le aste di cui l'utente corrente è proprietario o membro */
  async listMyAuctions(){ return notImplemented("listMyAuctions"); }
  /** @returns {Promise<{ok:boolean, auction:AuctionSummary|null, error:string|null}>} */
  async createAuction({name, season}){ return notImplemented("createAuction"); }
  /** @returns {Promise<{ok:boolean, error:string|null}>} */
  async renameAuction(auctionId, name){ return notImplemented("renameAuction"); }
  /** @returns {Promise<{ok:boolean, error:string|null}>} */
  async archiveAuction(auctionId){ return notImplemented("archiveAuction"); }
  /** @returns {Promise<AuctionSummary|null>} */
  async getAuctionSummary(auctionId){ return notImplemented("getAuctionSummary"); }

  // ---------------- Stato d'asta (obiettivo 8, usato da AuctionRepository) ----------------
  /** @returns {Promise<AuctionStateEnvelope|null>} null se l'asta non ha ancora uno stato salvato */
  async loadAuctionState(auctionId){ return notImplemented("loadAuctionState"); }
  /**
   * Optimistic concurrency: `expectedRevision` deve combaciare con la revision attualmente
   * salvata, altrimenti l'adapter DEVE restituire {ok:false, conflict:true} senza scrivere nulla
   * — mai una sovrascrittura silenziosa fra schede/dispositivi diversi (obiettivo 8).
   * @returns {Promise<{ok:boolean, conflict:boolean, revision:number|null, error:string|null}>}
   */
  async saveAuctionState(auctionId, state, expectedRevision){ return notImplemented("saveAuctionState"); }

  // ---------------- Lobby multiplayer / join-code (round alpha.4-r1) ----------------
  // Vedi MULTIPLAYER_SESSION_DESIGN_alpha4_r1.md per il contratto completo. Nessun metodo qui sotto
  // scrive mai auction_states/il motore/roster/purchaseLedger — SOLO chi appartiene alla sessione
  // (auction_members). Il client non sceglie MAI un userId da inserire: l'identità è sempre quella
  // della sessione autenticata corrente, letta internamente da ogni adapter.

  /**
   * @param {string} rawCode - codice così come digitato dall'utente (non canonicalizzato dal chiamante)
   * @returns {Promise<{ok:boolean, auctionId:string|null, alreadyMember:boolean, error:string|null}>}
   * Idempotente: un utente già membro (o già owner) dell'asta risolta dal codice riceve
   * {ok:true, alreadyMember:true}, mai un errore né una seconda riga di membership.
   */
  async joinAuctionByCode(rawCode){ return notImplemented("joinAuctionByCode"); }

  /**
   * @returns {Promise<Array<{userId:string, displayName:string, role:"owner"|"member", joinedAt:string}>>}
   * Richiede che il chiamante sia owner o member di QUESTA specifica auction — mai un elenco
   * parziale/vuoto per un chiamante non autorizzato: l'adapter deve propagare un errore esplicito
   * (o lanciare), mai restituire silenziosamente [].
   */
  async listAuctionMembers(auctionId){ return notImplemented("listAuctionMembers"); }

  /**
   * Genera un nuovo codice di invito, o lo ruota se ne esiste già uno per questa asta — stessa
   * operazione in entrambi i casi (sovrascrive). Owner-only. Il codice in chiaro è restituito SOLO
   * qui, una volta: nessun altro metodo può mai recuperarlo in seguito.
   * @returns {Promise<{ok:boolean, code:string|null, generatedAt:string|null, error:string|null}>}
   */
  async rotateJoinCode(auctionId){ return notImplemented("rotateJoinCode"); }

  /** Owner-only. Il codice precedente smette immediatamente di funzionare.
   * @returns {Promise<{ok:boolean, error:string|null}>} */
  async revokeJoinCode(auctionId){ return notImplemented("revokeJoinCode"); }

  /** Owner-only. Non espone mai il codice in chiaro né il suo hash — solo se un codice è
   *  attualmente attivo, per decidere se la UI owner mostra "Genera" o "Ruota/Revoca".
   * @returns {Promise<{ok:boolean, active:boolean, generatedAt:string|null, error:string|null}>} */
  async getJoinCodeStatus(auctionId){ return notImplemented("getJoinCodeStatus"); }

  /** Owner-only. Non può mai rimuovere la riga owner (né la propria né, non essendoci altri owner,
   *  quella di nessun altro) — un tentativo su memberUserId che risulta essere l'owner deve fallire
   *  esplicitamente, mai rimuovere silenziosamente nulla di diverso.
   * @returns {Promise<{ok:boolean, error:string|null}>} */
  async removeAuctionMember(auctionId, memberUserId){ return notImplemented("removeAuctionMember"); }

  /** Il chiamante lascia una propria membership non-owner. Chiamato dall'owner deve fallire
   *  esplicitamente (l'owner non può mai rimuovere la propria membership owner).
   * @returns {Promise<{ok:boolean, error:string|null}>} */
  async leaveAuction(auctionId){ return notImplemented("leaveAuction"); }

  // ---------------- Live auction: seat/squadra (round alpha.5-r1) ----------------
  // Vedi LIVE_AUCTION_DESIGN_alpha5_r1.md §1-§2. Una "seat" è la squadra fantasy/il partecipante
  // economico dell'asta — distinta dall'identità auction_members (MEMBER != TEAM). Assegnazione
  // SEMPRE owner-controlled: nessun metodo qui sotto permette a un member di assegnare se stesso.

  /** Owner-only. @returns {Promise<{ok:boolean, seatId:string|null, seatNo:number|null, label:string|null, error:string|null}>} */
  async createAuctionSeat(auctionId, label){ return notImplemented("createAuctionSeat"); }
  /** Owner-only. Rifiuta se la seat è assegnata o la sessione live non è in 'setup'.
   * @returns {Promise<{ok:boolean, error:string|null}>} */
  async deleteAuctionSeat(seatId){ return notImplemented("deleteAuctionSeat"); }
  /**
   * BLOCKER C (audit indipendente su alpha.5-r1): restituisce SOLO `assignedUserId`, mai un nome di
   * visualizzazione — `profiles` è self-only (mai aperta globalmente); il chiamante risolve il nome
   * cercando l'id nella member list già ottenuta da `listAuctionMembers()`. Un chiamante non owner/
   * member riceve `[]` (mai un errore): stessa semantica di una SELECT diretta filtrata da RLS —
   * entrambi gli adapter devono restituire `[]` per un outsider, mai uno lanciare e l'altro no.
   * @returns {Promise<Array<{id:string, seatNo:number, label:string, assignedUserId:string|null}>>}
   */
  async listAuctionSeats(auctionId){ return notImplemented("listAuctionSeats"); }
  /** Owner-only. Idempotente se il target è già assegnato a questa stessa seat; fallisce
   *  esplicitamente se la seat è di un altro utente o se il target controlla già un'altra seat.
   * @returns {Promise<{ok:boolean, error:string|null}>} */
  async assignMemberToSeat(seatId, userId){ return notImplemented("assignMemberToSeat"); }
  /** Owner-only. Idempotente (una seat già libera resta libera).
   * @returns {Promise<{ok:boolean, error:string|null}>} */
  async unassignMemberFromSeat(seatId){ return notImplemented("unassignMemberFromSeat"); }

  // ---------------- Live auction: lifecycle della sessione (round alpha.5-r1) ----------------
  // Vedi LIVE_AUCTION_DESIGN_alpha5_r1.md §3-§4. Server-authoritative: nessun metodo qui accetta uno
  // stato "live" arbitrario dal client, solo comandi semantici con concorrenza ottimistica esplicita
  // (expectedRevision, stesso principio di saveAuctionState()).

  /**
   * @returns {Promise<{auctionId:string, lifecycle:"setup"|"running"|"paused"|"closed", revision:number,
   *   nominationStatus:"open"|"pending_settlement"|null, currentNominationId:string|null,
   *   nominatedPlayerFantacalcioId:number|null, nominatingSeatId:string|null,
   *   currentHighestBid:number|null, highestBidderSeatId:string|null, bidSeq:number,
   *   deadlineAt:string|null}|null>}
   * null se la sessione live non è mai stata avviata (equivalente a revision:0/lifecycle:"setup").
   * Owner o member. Estensione round alpha.5-r4-r1: i campi nomination/bid/deadlineAt riflettono
   * SEMPRE lo stato server-authoritative corrente — mai calcolati/derivati lato client. Prima di r4
   * (nessuna sessione con nomination_status valorizzato può esistere) questi campi sono sempre null/0.
   */
  async loadLiveSession(auctionId){ return notImplemented("loadLiveSession"); }
  /** Owner-only. Precondizione: almeno una seat deve esistere per l'asta.
   * @returns {Promise<{ok:boolean, conflict:boolean, revision:number|null, lifecycle:string|null, error:string|null}>} */
  async startLiveSession(auctionId, expectedRevision){ return notImplemented("startLiveSession"); }
  /** Owner-only. running -> paused.
   * @returns {Promise<{ok:boolean, conflict:boolean, revision:number|null, lifecycle:string|null, error:string|null}>} */
  async pauseLiveSession(auctionId, expectedRevision){ return notImplemented("pauseLiveSession"); }
  /** Owner-only. paused -> running.
   * @returns {Promise<{ok:boolean, conflict:boolean, revision:number|null, lifecycle:string|null, error:string|null}>} */
  async resumeLiveSession(auctionId, expectedRevision){ return notImplemented("resumeLiveSession"); }
  /** Owner-only. running|paused -> closed. Terminale in questo round (nessun metodo riapre una
   *  sessione chiusa).
   * @returns {Promise<{ok:boolean, conflict:boolean, revision:number|null, lifecycle:string|null, error:string|null}>} */
  async closeLiveSession(auctionId, expectedRevision){ return notImplemented("closeLiveSession"); }

  // ---------------- Live auction: nomina/offerta/timer (round alpha.5-r4-r1) ----------------
  // Sopra la fondazione seat/lifecycle di alpha.5-r1 — vedi backend/migrations/
  // 0014_live_auction_nomination_bidding.sql per il contratto server-authoritative completo. Nessun
  // metodo qui sotto scrive mai auction_states/auction_settings, mai un settlement (nessun
  // auction_purchases/auction_roster_rules/campo "settled" in questo round). expectedRevision/
  // expectedBidSeq/commandId seguono lo stesso principio di concorrenza ottimistica esplicita già
  // usato altrove — MAI un retry automatico su conflitto, il chiamante riceve {conflict:true} e decide.

  /** Owner o member. @returns {Promise<{auctionId:string, openingPrice:number, initialTimerSeconds:number,
   *  resetOnBid:boolean, resetTimerSeconds:number, revision:number}|null>} null se mai configurate. */
  async loadLiveRules(auctionId){ return notImplemented("loadLiveRules"); }

  /** Owner-only. Solo in 'setup' (o prima ancora che la sessione live esista). expectedRevision=0
   *  significa "nessuna riga precedente attesa" (stessa convenzione di saveAuctionState()/
   *  startLiveSession() con revision 0).
   * @returns {Promise<{ok:boolean, conflict:boolean, revision:number|null, error:string|null}>} */
  async configureLiveRules(auctionId, rules, expectedRevision){ return notImplemented("configureLiveRules"); }

  /** Owner-only. Richiede lifecycle='running', nessuna nomina già aperta, e regole live configurate.
   *  Idempotente su commandId: un retry con lo STESSO payload (seatId, fantacalcioId) restituisce lo
   *  stesso esito precedente, mai una seconda nomina.
   * @param {string} commandId - UUID generato dal chiamante, stabile per l'intera vita del comando
   *  (mai rigenerato a ogni retry) — unica chiave di idempotenza lato server.
   * @returns {Promise<{ok:boolean, conflict:boolean, revision:number|null, nominationId:string|null,
   *  nominationSeq:number|null, openingPrice:number|null, deadlineAt:string|null, error:string|null}>} */
  async nominatePlayer(auctionId, seatId, fantacalcioId, expectedRevision, commandId){ return notImplemented("nominatePlayer"); }

  /** Chiamabile SOLO dal controller della seat che offre (mai owner-only: qualunque membro/owner che
   *  controlli una seat può offrire) — l'identità è sempre quella della sessione autenticata corrente,
   *  mai un userId dichiarato dal client. Primo bid >= openingPrice, successivi > offerta corrente
   *  (mai pari, mai un self-raise sulla propria stessa offerta). Idempotente su commandId, stessa
   *  disciplina di nominatePlayer().
   * @returns {Promise<{ok:boolean, conflict:boolean, bidSeq:number|null, amount:number|null,
   *  deadlineAt:string|null, revision:number|null, error:string|null}>} */
  async placeBid(auctionId, nominationId, seatId, amount, expectedBidSeq, commandId){ return notImplemented("placeBid"); }

  /** Owner o member — chiunque appartenga all'asta può far avanzare una nomina scaduta (nessun singolo
   *  utente deve poter bloccare l'asta restando inattivo). Replay-safe: se la nomina è già stata
   *  finalizzata da un'altra chiamata concorrente, restituisce lo stesso esito storico con
   *  alreadyFinalized:true, mai un errore. Esiti possibili: "unsold" (nessuna offerta) o
   *  "pending_settlement" (un'offerta vincente, in attesa di un futuro round di settlement — MAI
   *  applicato qui: nessuna scrittura su roster/budget in questo round).
   * @returns {Promise<{ok:boolean, alreadyFinalized:boolean, nominationId:string|null,
   *  outcome:"unsold"|"pending_settlement"|null, winningSeatId:string|null, winningAmount:number|null,
   *  revision:number|null, error:string|null}>} */
  async finalizeIfExpired(auctionId, nominationId){ return notImplemented("finalizeIfExpired"); }

  // ---------------- Live auction: settlement / purchase ledger / budget / roster (round alpha.5-r5-r1) ----------------
  // Sopra nomination/bidding/timer di alpha.5-r4 — vedi backend/migrations/
  // 0015_live_auction_settlement_budget_roster.sql per il contratto server-authoritative completo.
  // auction_purchases è l'UNICA authority economica: budget residuo = initial_budget_per_seat -
  // SUM(purchases.amount per seat); roster = COUNT/righe purchases per seat, per role_at_purchase. Mai
  // un remaining_budget/roster_count persistito — sempre derivato da listAuctionPurchases()/
  // loadRosterRules(). NESSUNA Realtime, reconnect/recovery, Strategy System, Mantra gameplay in
  // questo round.

  /** Owner o member. @returns {Promise<{auctionId:string, initialBudgetPerSeat:number,
   *  prezzoMinimo:number, slotPor:number, slotDif:number, slotCen:number, slotAtt:number,
   *  revision:number}|null>} null se mai configurate. */
  async loadRosterRules(auctionId){ return notImplemented("loadRosterRules"); }

  /** Owner-only. Solo in 'setup' (o prima ancora che la sessione live esista). initialBudgetPerSeat
   *  deve essere un intero fra 50 e 5000. prezzoMinimo resta SEMPRE fisso a 1 (policy applicativa,
   *  non un parametro del chiamante — coerente col contratto server). Le taglie di rosa per ruolo
   *  (round 2.1.0) sono invece OPZIONALI: se omesse restano la forma classica 3/8/8/6 (stesso
   *  default lato server, mai un valore diverso), coerente coi chiamanti 2.0.1 che non le passano
   *  mai. Ciascuna deve essere un intero >= 0 (zero per un singolo ruolo è ammesso — una lega che
   *  non usa quel ruolo); la somma deve essere > 0 e <= initialBudgetPerSeat (prezzoMinimo=1 rende
   *  altrimenti la rosa strutturalmente irraggiungibile anche al prezzo minimo).
   * @returns {Promise<{ok:boolean, conflict:boolean, revision:number|null, error:string|null}>} */
  async configureRosterRules(auctionId, initialBudgetPerSeat, expectedRevision, slotPOR = 3, slotDIF = 8, slotCEN = 8, slotATT = 6){ return notImplemented("configureRosterRules"); }

  /** Owner o member. Storico append-only di ogni acquisto finalizzato per questa asta — mai un
   *  campo aggregato: il chiamante deriva budget residuo/roster sommando/contando queste righe per
   *  seat, mai un valore persistito altrove.
   * @returns {Promise<Array<{id:string, nominationId:string, seatId:string,
   *  playerFantacalcioId:number, amount:number, roleAtPurchase:"POR"|"DIF"|"CEN"|"ATT",
   *  purchaseSeq:number, resultingRevision:number, settledByUserId:string, createdAt:string}>>} */
  async listAuctionPurchases(auctionId){ return notImplemented("listAuctionPurchases"); }

  /** Owner o member CORRENTE (mai winning-seat-only: chiunque appartenga all'asta può finalizzare).
   *  Applica una nomina pending_settlement al ledger acquisti (auction_purchases), la porta a
   *  'settled' e libera la sessione live per la prossima nomina. Idempotente: un secondo settle sulla
   *  stessa nomina già settled restituisce lo stesso esito storico con alreadySettled:true, mai un
   *  errore né una seconda purchase. Un membro rimosso riceve un rifiuto esplicito, anche su un
   *  replay di una nomina già settled.
   * @returns {Promise<{ok:boolean, alreadySettled:boolean, purchaseId:string|null, seatId:string|null,
   *  playerFantacalcioId:number|null, amount:number|null, revision:number|null, error:string|null}>} */
  async settleNomination(auctionId, nominationId){ return notImplemented("settleNomination"); }
}
